import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  Injector,
  NgZone,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {PlayerBase} from '../player/player.base';
import {PlayerBaseService} from '../services/PlayerService/player.base.service';
import {SceneComponent} from '../components/scene/scene.component';
import {IPlayerService} from '../interfaces/IPlayerService';
import {Log, LogTag} from '../services/Log';
import {fromEvent, Observable, of, mergeWith, filter, takeUntil} from 'rxjs';
import {tap, concatMap, debounceTime, switchMap, finalize, take} from "rxjs/operators";
import {CachingService} from "../services/caching/caching.service";
import {Progress} from "../services/caching/progress";



@Component({
  selector: 'app-home-base',
  template: '',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignagePlayerBase implements OnInit  {
  ngZone: NgZone;
  resolver: ComponentFactoryResolver;
  changeDetectorRef: ChangeDetectorRef;
  showProgress = false;
  sceneData;

  playerService: IPlayerService;
  mainScene: PlayerBase;

  geometrychange;
  virtualKeyboardActive = false;
  fnFocus;
  focusOffset = 0;
  screenHeight = 0;



  subscription;

  // @ts-ignore
  @ViewChild('component', {static: true} ) component;

  // @ts-ignore
  @ViewChild('myHome', {static: true} ) myHome;

  // @ts-ignore
  @ViewChild('playercontainer', { read: ViewContainerRef, static: true })
  entry: ViewContainerRef;



  constructor(protected  injector: Injector) {
    this.changeDetectorRef = injector.get(ChangeDetectorRef);
    this.resolver = injector.get(ComponentFactoryResolver);
    this.ngZone = injector.get(NgZone);
    this.ngZone.runOutsideAngular(() => {
      this.playerService = injector.get('playerService');
    });
    document.addEventListener('contextmenu',event => event.preventDefault());
  }



  ngOnInit() {
    const logInfo = Log.getLogInfo("SignagePlayerBase", "ngOnInit", []);
    Log.d(logInfo, LogTag.INIT, 'NgZone: ' + NgZone.isInAngularZone())


    if (navigator['virtualKeyboard']!=null) {
      navigator['virtualKeyboard'].overlaysContent = true;

      this.fnFocus = (event) => {
        const rect = event.target.getBoundingClientRect()
        this.focusOffset = rect.top + rect.height * 4;
      }
      this.component.nativeElement.addEventListener("focus", this.fnFocus, true);

      this.geometrychange = (event) => {
        this.virtualKeyboardActive = true;
        this.component.nativeElement.offsetHeight
        setTimeout(()=> {
          const virtualKeyboardHeight = navigator['virtualKeyboard'].boundingRect.height;
          const y = -Math.max(0, this.focusOffset + virtualKeyboardHeight - this.screenHeight);
          if (y < 0) {
            this.component.nativeElement.style.top = y + 'px';
          } else if (this.component.nativeElement.style.top != "") {
            this.component.nativeElement.style.top = "";
          }
        }, 100)
      }
      navigator['virtualKeyboard'].addEventListener("geometrychange", this.geometrychange);
    }



    this.subscription = this.init().pipe(
      switchMap( r1 => {
        if (r1==null) {
          Log.w(logInfo, LogTag.DISPOSE, 'exit browser');
          return of(null);
        }
        return this.run().pipe(
          mergeWith( (<PlayerBaseService>this.playerService).stationStats())
        )
      })
    ).subscribe();
  }

  init(): Observable<any> {
    return (this.playerService as PlayerBaseService).init();
  }

  run(): Observable<any> {
    const logInfo = Log.getLogInfo("SignagePlayerBase", "run", []);
    return (this.playerService as PlayerBaseService).playing$.pipe(
      switchMap( r => (this.playerService.isPlaying()) ? this.requestScene() : of('next')),
      mergeWith( fromEvent(window, 'resize').pipe(
        debounceTime(250),
        filter(a => {
          if (this.virtualKeyboardActive) {
            this.virtualKeyboardActive = false;
            return false;
          } else {
            return true;
          }
        })
      )),
      switchMap(r=> this.stop()),
      filter(f => this.playerService.isPlaying()),
      switchMap(r=> this.start().pipe(
        // when playing$ == 'stop' it will unsubscribe this.start()
        takeUntil((this.playerService as PlayerBaseService).playing$.pipe(
          filter( v => (v=='stop') )
        )),
        finalize( () => {
          Log.d(logInfo, LogTag.MAIN, 'finalize()')
        })
      ))
    )
  }

  requestScene(): Observable<any> {
    const logInfo = Log.getLogInfo("SignagePlayerBase", "requestScene", []);
    return of('load-scene').pipe(
      tap( v => (<CachingService>this.playerService.caching).clearModified() ),
      switchMap( r1 => {
          Log.d(logInfo, LogTag.MAIN, 'getSceneId()');
          return (this.playerService as PlayerBaseService).getSceneId();
        }
      ),
      switchMap( sceneId => {
        return (<CachingService>this.playerService.caching).getRecord("scenes", sceneId);
      }),
      tap( scene => {
        if (scene!=null && scene.json!=null) {
          this.sceneData = scene.json;
        } else {
          Log.e(logInfo, LogTag.MAIN, 'Scene data corrupted or not exist in DB');
        }
      })
    )
  }

  stop():Observable<any> {
    const logInfo = Log.getLogInfo("SignagePlayerBase", "stop", []);
    return new Observable<any>( sub => {
      Log.d(logInfo, LogTag.MAIN, '');
      (<CachingService>this.playerService.caching).abort();
      this.playerService.clearCircular();
      this.playerService.clearSchedules();
      if (this.mainScene != null) {
        //??? this.mainScene.destroy();   // putting this line cause exception when resizing, but looks like it's not needed. there is no memory leak when it's comment out.
        this.mainScene = null;
      }
      if (this.entry.length > 0) {
        this.entry.remove(0);
        this.changeDetectorRef.detectChanges();
      }

      sub.next('remove-scene');
      sub.complete();
    })
  }

  start(): Observable<any> {
    const logInfo = Log.getLogInfo("SignagePlayerBase", "start", []);
    const obs1$ = new Observable( sub => {
      Log.d(logInfo, LogTag.MAIN, '');
      if (this.sceneData) {
        const factory = this.resolver.resolveComponentFactory(SceneComponent);
        const componentRef = this.entry.createComponent(factory);
        this.mainScene = <PlayerBase>componentRef.instance;
        this.mainScene.init(null, false, -1);

        this.mainScene.usePlayerLayoutX = false;
        this.mainScene.usePlayerLayoutY = false;
        this.mainScene.originalSize = this.sceneData.Player.Data[0].Layout[0].$;
        sub.next('create-scene');
        sub.complete();
      } else {
        sub.next(null);
        sub.complete();
      }
    });

    return obs1$.pipe(
      switchMap( r1 => {
        if (this.mainScene != null)  {
          Progress.begin();
          //??? this.showProgress = true;
          this.changeDetectorRef.detectChanges();
          return this.mainScene.load(this.sceneData.Player).pipe(
            switchMap( a=> {
              (this.mainScene as SceneComponent).originalSize = this.sceneData.Player.Data[0].Layout[0].$;
              this.mainSceneLoaded();
              return of('a');
            }),
            mergeWith(this.mainScene.play())
          )
        } else {
          return of(null);
        }
      })
    )
  }

  mainSceneLoaded() {

  }


  get actualWidth(): number {
      debugger;
      return 0; //???
    //??? return (this.playerService as PlayerBaseService).pixi.view.width;
  }

  get actualHeight(): number {
      debugger;
      return 0; //???

      //return (this.playerService as PlayerBaseService).pixi.view.height;
  }

  getActualSize(): any {
    return this.component.nativeElement.getBoundingClientRect();
  }

  dispose(): void {
    const logInfo = Log.getLogInfo("SignagePlayerBase", "dispose", []);
    Log.d(logInfo, LogTag.DISPOSE, '');

    if (this.geometrychange!=null) {
      navigator['virtualKeyboard'].removeEventListener("geometrychange", this.geometrychange);
      this.geometrychange = null;
    }

    if (this.fnFocus!=null) {
      this.component.nativeElement.removeEventListener("focus", this.fnFocus, true);
      this.fnFocus = null;
    }

    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null;
    }
    if (this.mainScene != null) {
      this.mainScene.destroy();
      this.mainScene = null;
    }
    (this.playerService as PlayerBaseService).dispose();
  }

}
